import { formatPrecise } from '@/lib/format'

interface PartToWholeBreakdownProps {
  data: { name: string; count: number }[]
  unitLabel: string
}

// Categorical slots 1-6 from the validated reference palette, in their fixed
// (never-cycled) order -- for genuinely part-to-whole data, each category
// earns its own hue rather than sharing the single sequential blue used for
// magnitude rankings elsewhere on the page. "Other" always gets neutral
// gray, never a 7th hue.
const SLOT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7']
const OTHER_COLOR = '#52514e'

export function PartToWholeBreakdown({ data, unitLabel }: PartToWholeBreakdownProps) {
  const total = data.reduce((sum, d) => sum + d.count, 0)
  if (total === 0) return null

  const segments = data.map((d, i) => ({
    ...d,
    percent: (d.count / total) * 100,
    color: d.name === 'Other' ? OTHER_COLOR : SLOT_COLORS[i % SLOT_COLORS.length],
  }))

  return (
    <div className="flex flex-col gap-6">
      <div className="flex h-8 w-full gap-0.5 overflow-hidden rounded-md">
        {segments.map((segment) => (
          <div
            key={segment.name}
            className="h-full first:rounded-l-md last:rounded-r-md"
            style={{ width: `${segment.percent}%`, backgroundColor: segment.color }}
            title={`${segment.name}: ${formatPrecise(segment.count)} ${unitLabel} (${segment.percent.toFixed(1)}%)`}
          />
        ))}
      </div>

      <ul className="flex flex-col gap-3">
        {segments.map((segment) => (
          <li key={segment.name} className="flex items-center gap-3">
            <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: segment.color }} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-sm font-medium capitalize">{segment.name}</span>
                <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                  {formatPrecise(segment.count)} {unitLabel} &middot; {segment.percent.toFixed(0)}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${segment.percent}%`, backgroundColor: segment.color }}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
